import Snap, { path } from '@aibee/snapsvg';
import Bezier from 'bezier-js';
import { GeometryUtils, PathUtils } from '@aibee/owlly';
import { vec4, mat4 } from 'gl-matrix';

function transformSegments(commands) {
  const ret = [];
  let loop = [];
  commands.forEach((c) => {
    if (c[0] === 'M') {
      if (loop && loop.length > 0) {
        if (loop.length > 2) loop.push(loop[0]);
        if (loop.length < 2) return;

        loop = PathUtils.simplify(loop.map(q => ({ x: q[0], y: q[1] })));
        loop = loop.map(q => ([q.x, q.y]));
        ret.push({
          path: loop,
          distances: GeometryUtils.GetPathDistances(loop),
          centroid: GeometryUtils.Centroid(loop),
        });
      }
      loop = [c.slice(1)];
    } else if (c[0] === 'L') {
      loop.push(c.slice(1));
    } else if (c[0] === 'C') {
      const bezier = new Bezier(c.slice(1));
      const points = bezier.getLUT(5);
      points.forEach((p) => {
        loop.push([p.x, p.y]);
      });
    }
  });

  if (loop && loop.length > 0) {
    if (loop.length > 2) loop.push(loop[0]);
    if (loop.length < 2) return [];

    loop = PathUtils.simplify(loop.map(q => ({ x: q[0], y: q[1] })));
    loop = loop.map(q => ([q.x, q.y]));
    ret.push({
      path: loop,
      distances: GeometryUtils.GetPathDistances(loop),
      centroid: GeometryUtils.Centroid(loop),
    });
  }

  return ret;
}

function parsePathElementToShape(node, parentAttr) {
  const { d } = node.attr();
  const pathCommands = Snap.parsePathString(d);
  const alignedPathCommands = [];
  const matrix = node.transform().totalMatrix;

  pathCommands.forEach(([commandChar, ...coords]) => {
    const step = [];
    step.push(commandChar);
    for (let i = 0; i < coords.length; i += 2) {
      const localX = coords[i];
      const localY = coords[i + 1];
      const worldX = matrix.x(localX, localY);
      const worldY = matrix.y(localX, localY);
      step.push(worldX, worldY);
    }

    alignedPathCommands.push(step);
  });

  return {
    ...parentAttr,
    ...node.attr(),
    isStore: parentAttr.name === 'store',
    s: parentAttr.name && parentAttr.name === 'ground' ? [] : transformSegments(alignedPathCommands),
    d: alignedPathCommands.map(([command, ...coords]) => (`${command}${coords.join(',')}`)).join(''),
  };
}

function parsePolygonElementToShape(node, parentAttr) {
  const { points, id } = node.attr();
  const pointList = points.split(' ').map(parseFloat);
  const alignedPathCommands = [];
  const matrix = node.transform().totalMatrix;

  for (let i = 0; i < pointList.length; i += 2) {
    const step = [];
    step.push(i > 0 ? 'L' : 'M');
    const localX = pointList[i];
    const localY = pointList[i + 1];
    const worldX = matrix.x(localX, localY);
    const worldY = matrix.y(localX, localY);
    step.push(worldX, worldY);
    alignedPathCommands.push(step);
  }
  alignedPathCommands.push('Z');

  return {
    ...parentAttr,
    ...node.attr(),
    isStore: parentAttr.name === 'store',
    s: transformSegments(alignedPathCommands),
    d: alignedPathCommands.map(([command, ...coords]) => (`${command}${coords.join(',')}`)).join(''),
  };
}

function parseRectElementToShape(node, parentAttr) {
  const {
    x: xString, y: yString, width: widthString, height: heightString, id,
  } = node.attr();
  const [x, y, width, height] = [xString, yString, widthString, heightString].map(parseFloat);
  const alignedPathCommands = [];
  const matrix = node.transform().totalMatrix;

  const pointList = [x, y, x + width, y, x + width, y + height, x, y + height, x, y];

  for (let i = 0; i < pointList.length; i += 2) {
    const step = [];
    step.push(i > 0 ? 'L' : 'M');
    const localX = pointList[i];
    const localY = pointList[i + 1];
    const worldX = matrix.x(localX, localY);
    const worldY = matrix.y(localX, localY);
    step.push(worldX, worldY);
    alignedPathCommands.push(step);
  }

  return {
    ...parentAttr,
    ...node.attr(),
    isStore: parentAttr.name === 'store',
    s: transformSegments(alignedPathCommands),
    d: alignedPathCommands.map(([command, ...coords]) => (`${command}${coords.join(',')}`)).join(''),
  };
}

function parsePoiElementToShape(node, parentAttr) {
  return {
    ...parentAttr,
    ...node.attr(),
    isStore: parentAttr.name === 'store',
  }  
}

function parseSnapSvgToShapes(node, parentAttr) {
  const { type } = node;
  const attr = node.attr();
  const children = node.children();
  const result = [];
  for (let i = 0; i < children.length; i += 1) {
    const childShapes = parseSnapSvgToShapes(children[i], {
      ...parentAttr,
      ...attr,
    });
    for (let j = 0; j < childShapes.length; j += 1) {
      result.push(childShapes[j]);
    }
  }
  if (type === 'polyline') {
    if (node.attr().points.length) {
      result.push(parsePolygonElementToShape(node, parentAttr))
    }
  }

  if (type === 'path') {
    if (node.attr().d.length) {
      result.push(parsePathElementToShape(node, parentAttr));
    }
  }
  if (type === 'polygon') {
    result.push(parsePolygonElementToShape(node, parentAttr));
  }
  if (type === 'rect') {
    result.push(parseRectElementToShape(node, parentAttr));
  }
  // 如果是自定义的poi图标点
  if (type === 'circle' && attr.ispoi == 1) {
    result.push(parsePoiElementToShape(node, parentAttr));
  }
  return result;
}

function distanceSquaredToLineSegment2(lx1, ly1, ldx, ldy, lineLengthSquared, px, py) {
  let t; // t===0 at line pt 1 and t ===1 at line pt 2
  if (!lineLengthSquared) {
    // 0-length line segment. Any t will return same result
    t = 0;
  } else {
    t = ((px - lx1) * ldx + (py - ly1) * ldy) / lineLengthSquared;

    if (t >= 0) {
      if (t > 1) {
        t = 1;
      }
    } else {
      t = 0;
    }
  }

  const lx = lx1 + t * ldx;
  const ly = ly1 + t * ldy;
  const dx = px - lx;
  const dy = py - ly;
  return [dx * dx + dy * dy, {
    x: lx,
    y: ly,
  }];
}

function distance(p1, p2) {
  const lx = (p1.x - p2.x) ** 2;
  const ly = (p1.y - p2.y) ** 2;
  return lx + ly;
}

export default {
  transformSegments,
  loadSVGFromURL: url => new Promise((resolve) => {
    Snap.load(
      url,
      (fragment) => {
        resolve(parseSnapSvgToShapes(fragment.select('svg')));
      },
    );
  }),
  distanceSquaredToLineSegment: (lx1, ly1, lx2, ly2, px, py) => {
    const ldx = lx2 - lx1;
    const ldy = ly2 - ly1;
    const lineLengthSquared = ldx * ldx + ldy * ldy;
    return distanceSquaredToLineSegment2(lx1, ly1, ldx, ldy, lineLengthSquared, px, py);
  },
  closestPath(svgShapes, { x, y }, layer) {
    svgShapes.forEach((shape) => {
      let maxDistance = Number.MAX_VALUE;
      shape.attrs.shape.forEach((p) => {
        const [px, py] = p.centroid;
        const lx = x - px;
        const ly = y - py;
        const dist = lx * lx + ly * ly;
        if (dist < maxDistance) {
          maxDistance = dist;
        }
      });
      shape.maxDistance = maxDistance;
    });
    svgShapes.sort((a, b) => a.maxDistance - b.maxDistance);

    let max = Number.MAX_VALUE;
    let point = null;
    let resShape = null;
    svgShapes.slice(0, 5).forEach((shape) => {
      shape.attrs.shape.forEach((q) => {
        const l = GeometryUtils.GetNearestPoint(
          [x, y],
          q.path,
          q.distances,
          0.1,
          10,
        );

        const lx = l.point[0] - x;
        const ly = l.point[1] - y;
        const dist = (lx ** 2) + (ly ** 2);
        if (dist < max) {
          max = dist;
          point = l.point;
          resShape = shape;
        }
      });
    });

    return {
      min: max,
      point,
      shape: resShape,
    };
  },

  getPositionTransform(
    [x, y],
    m,
  ) {
    const alignMat = mat4.fromValues(
      m[0], m[1], m[2], m[3], m[4], m[5], m[6], m[7], m[8], m[9], m[10], m[11], m[12], m[13], m[14], m[15],
    );
    const v = vec4.create();
    vec4.set(v, x, y, 0, 1);
    const result = vec4.create();
    vec4.transformMat4(result, v, alignMat);
    return { x: result[0], y: result[1] };
  },
};
